import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StateInterface } from 'Client/redux/types';
import { showOverlay } from 'Client/redux/header/overlays/actions';
import isClient, { cookiesEnabled } from 'Common/helpers/environment';
import { ShopToClubConfigState } from 'Common/appshell_types/remoteConfigType';
import useConfig from 'Client/hooks/useConfig';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import useWasClubRegistrationViewed from 'Client/hooks/flags/useWasClubRegistrationViewed';
import { ShopToClubShownLabel } from 'Common/ga/shopToClub';
import useRouter from 'Client/hooks/useRouter';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { PageType, ProductListingPages } from 'Client/hooks/UrlManager/types';
import { useCurrentPageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { isAnyOverlayActive } from 'Client/redux/header/overlays/selectors';

const useForceClubRegistrationPopup = () => {
    const [hasBeenViewed, setHasBeenViewedStatus] = useWasClubRegistrationViewed();
    const dispatch = useDispatch();
    const { isLoggedIn, isRegistered } = useUserTracking();
    const { isAppWebviewPage } = useRouter();
    const config = useConfig();
    const [clicked, setClicked] = useState(false);

    const isAnyActive: boolean = useSelector<StateInterface, boolean>(state =>
        isAnyOverlayActive(state.header.overlays)
    );
    const data = useSelector<StateInterface, ShopToClubConfigState>(state => {
        return state.configurations.shopToClub;
    });
    const {
        location: { search },
    } = useRouter();
    const { pageType } = useCurrentPageType();
    const { p100OnClickForceClubRegistrationPopup } = useFeatureToggle();
    const isDisabledFromBob = !data?.isEnabled;

    const searchParams = new URLSearchParams(search);
    const canShowOnThisPage =
        [PageType.HOMEPAGE, PageType.PDP, ...ProductListingPages].includes(pageType) &&
        searchParams.get('utm_medium') === 'PSM' &&
        searchParams.get('utm_source') === 'FB' &&
        searchParams.get('utm_campaign')?.includes('prospecting') &&
        searchParams.get('utm_campaign')?.includes('shop');

    const failConditions = [
        isRegistered,
        isAppWebviewPage,
        isLoggedIn,
        isDisabledFromBob,
        isAnyActive,
        !p100OnClickForceClubRegistrationPopup,
        !canShowOnThisPage,
        !isClient(),
    ];

    const skipRender = failConditions.some(Boolean);

    const onPageBlockerClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setClicked(true);
    };

    useEffect(() => {
        const expirationInDays = data && data.expirationInDays;
        const cookieTTL = expirationInDays * 24 * 60 * 60 * 1000;
        const wasExpirationDisabled = !expirationInDays && hasBeenViewed;
        if (wasExpirationDisabled && cookiesEnabled) {
            setHasBeenViewedStatus(0, cookieTTL);
        }

        if (!skipRender && clicked) {
            dispatch(
                showOverlay(config.shopToClubOverlayId, {
                    isLogin: false,
                    isDismissable: false,
                    isUndismissableFromOutsideClick: true,
                    data: { trackingLabel: ShopToClubShownLabel.Default },
                })
            );
            setClicked(false);
            if (cookieTTL) {
                setHasBeenViewedStatus(1, cookieTTL);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canShowOnThisPage, clicked]);

    return {
        shouldRenderBlocker: !skipRender,
        onPageBlockerClick,
    };
};

export default useForceClubRegistrationPopup;
